import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

// The planning list. Same want_list_items table the customer "looking for" list
// uses — an influencer row is that record with planning dates on it, so anything
// that already reads want lists keeps working.
const blank = { raw_text: '', quantity: 1, purchase_by: '', content_date: '', note: '' };

const day = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

export default function InfluencerPage() {
  const { user, isInfluencer, isLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('want_list_items').select('*')
      .eq('user_id', user.id).eq('is_influencer', true)
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false });
    setRows(data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Chase hears about every change. Fire and forget — a failed email must never
  // block someone from building their list.
  const tell = (item_id, kind) =>
    supabase.functions.invoke('shinyvault-influencer', { body: { action: 'notify', item_id, kind } })
      .catch(() => {});

  const add = async (e) => {
    e.preventDefault();
    const text = form.raw_text.trim();
    if (!text) return;
    setError('');
    const { data, error: insErr } = await supabase.from('want_list_items').insert({
      user_id: user.id,
      raw_text: text,
      quantity: Number(form.quantity) || 1,
      purchase_by: form.purchase_by || null,
      content_date: form.content_date || null,
      note: form.note.trim() || null,
      is_influencer: true,
      status: 'open',
    }).select().maybeSingle();
    if (insErr) { setError(insErr.message); return; }
    setRows((r) => [data, ...r]);
    setForm(blank);
    setShowForm(false);
    tell(data.id, 'added');
  };

  const remove = async (id) => {
    setRows((r) => r.filter((x) => x.id !== id));
    await supabase.from('want_list_items')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() }).eq('id', id);
    tell(id, 'removed');
  };

  if (isLoading) return <div className="sv-container" style={{ paddingBlock: '70px' }}>Loading…</div>;

  if (!user || !isInfluencer) {
    return (
      <div className="sv-container" style={{ paddingBlock: '80px', maxWidth: 480, textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 900, marginBottom: 8 }}>Creator planning</h1>
        <p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          This one is invite only. If you were given a link, open that link to get set up.
        </p>
        <Link to="/shop" style={{ fontWeight: 800, display: 'inline-block', marginTop: 16 }}>Back to the shop</Link>
      </div>
    );
  }

  return (
    <div className="sv-container" style={{ paddingBlock: '48px 80px', maxWidth: 720 }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: 8 }}>Your planning list</h1>
      <p style={{ color: 'var(--text-muted)', lineHeight: 1.65, marginBottom: 8 }}>
        What you expect to need for upcoming content. Add as much as you want, whenever you want —
        this is how we know what to buy toward before it gets hard to find.
      </p>
      <p style={{
        fontSize: '0.82rem', lineHeight: 1.6, color: 'var(--text-muted)',
        border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 22,
      }}>
        <b style={{ color: 'var(--text)' }}>Nothing here is priced or ordered.</b> Prices move, so
        everything is quoted when it is actually bought, not today. Put dates on it and we will work
        backward from your filming schedule.
      </p>

      {!showForm && (
        <button className="sv-btn" onClick={() => setShowForm(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <Plus size={16} /> Add something
        </button>
      )}

      {showForm && (
        <form onSubmit={add} style={{
          display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24,
          border: '1px solid var(--border)', borderRadius: 12, padding: 16,
        }}>
          {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{error}</div>}
          <input className="sv-input" autoFocus placeholder="What do you need? e.g. Prismatic Evolutions booster box"
                 value={form.raw_text} onChange={(e) => setForm({ ...form, raw_text: e.target.value })} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: '0 0 90px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              How many
              <input className="sv-input" type="number" min="1" value={form.quantity}
                     onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </label>
            <label style={{ flex: 1, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Need it by
              <input className="sv-input" type="date" value={form.purchase_by}
                     onChange={(e) => setForm({ ...form, purchase_by: e.target.value })} />
            </label>
            <label style={{ flex: 1, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Filming around
              <input className="sv-input" type="date" value={form.content_date}
                     onChange={(e) => setForm({ ...form, content_date: e.target.value })} />
            </label>
          </div>
          <input className="sv-input" placeholder="Anything else worth knowing (optional)"
                 value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="sv-btn" type="submit">Add to my list</button>
            <button type="button" className="sv-btn-outline"
                    onClick={() => { setShowForm(false); setForm(blank); }}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>Nothing on the list yet.</p>
      ) : (
        <div>
          {rows.map((r) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>
                  {r.quantity > 1 && <span style={{ color: 'var(--text-muted)' }}>{r.quantity}× </span>}
                  {r.raw_text}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
                  {day(r.purchase_by) ? `Need it by ${day(r.purchase_by)}` : 'No date set'}
                  {day(r.content_date) ? ` · filming around ${day(r.content_date)}` : ''}
                </div>
                {r.note && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>{r.note}</div>
                )}
              </div>
              <button onClick={() => remove(r.id)} title="Remove"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
